import { IAccountIndustryBaseWithId } from './account-industry.type'
import { IAccountRatingWithId } from './account-rating.type'
import { IChange } from './change.type'
import { IBaseDtoType, IMessageResponseDto, ISocialUsernames } from './common.type'
import { IImpactBaseWithId } from './impacts.type'
import { IMediaRespDto, ICustomIconFile, IMediaBase, IMediaFiles } from './media.type'
import { IStepBaseWithId } from './step.type'
import { IProfileUserDtoWithId } from './user.type'
import { IWhiteLabelForm } from './white-label-partners.type'

export interface IBillingPlan {
  title: string
  monthlyFee?: string | null
  yearlyFee?: string | null
  description: string[]
}

export interface IPaymentCard {
  number: string
  holder: string
  date: string
  service: 'visa' | 'mastercard'
  isActive: boolean
}

export interface IAccountSettings {
  newsletters: boolean
  companyUpdates: boolean
  promotions: boolean
  failedScanAlerts: boolean
  serviceUpdates: boolean
  adminLoginAlerts: boolean
}

export interface IAccountBase {
  simple_id: string
  image: string
  image_small: string | null
  image_medium: string | null
  account_holder: string
  email: string | null
  phone: string | null
  billing_email: string | null
  contact_person: string | null
  description: string | null
  address: string | null
  latitude: number | null
  longitude: number | null
  region: string | null
  country: string | null
  website: string | null
  gln: string | null
  analytics_code: string | null
  two_factor_required: boolean
  suspended: boolean
  arweave_url: string | null
  token_id: string | null
  is_minting: boolean
  show_welcome: boolean
  hide_name: boolean
  hide_product_name: boolean
  map_satellite_view: boolean
  show_unsgd_home_page: boolean
  show_unsgd_impacts_page: boolean
  show_wallet_address: boolean
  show_bc_info: boolean
  default_landing_page: EDefaultLandingPage
  public: boolean
  ai_generated: boolean
}

export interface IAccountBaseWithId extends IAccountBase, IBaseDtoType {
  subscription_cancelled: boolean
  subscription_plan: EAccountSubscriptionPlans
}

export interface IAccountByIdRespDto extends IAccountBaseWithId, IAccountRelationsCount {
  industry: IAccountIndustryBaseWithId | null
  media: IMediaRespDto
  socials: ISocialUsernames | null
  created: IChange | null
  updated: IChange | null
  currentRoles: EAccountRoles[]
  currentAccountRoleForUser: EAccountRoles | null
  owner: {
    user: IProfileUserDtoWithId
  } | null
  suspended: boolean
  wallet: {
    id: string
    address: string
  }
  isSingleAccount: boolean
  rating: IAccountRatingWithId
  white_label: IWhiteLabelForm
  impact_options: IImpactOptionsForAccount[]
}

export interface IImpactOptionsForAccount {
  home_page_show: boolean
  impacts_page_show: boolean
  account_page_show: boolean
  impact_id: string
  account_id: string
  impact?: IImpactBaseWithId
}

export interface IAccountRelationsCount {
  productsCount: number
  usersCount: number
  impactsCount: number
  journeysCount: number
  stepsCount: number
}

export type AccountRelationsCountKeys = keyof IAccountRelationsCount

export interface IAccountFormCreate {
  image: ICustomIconFile
  image_small: string | null
  image_medium: string | null
  account_holder: string
  email: string | null
  phone: string | null
  gln: string | null
  contact_person: string
  billing_email: string | null
  description: string
  address: string
  latitude: number
  longitude: number
  region: string | null
  country: string | null
  website: string | null
  socials: ISocialUsernames | null
  media: IMediaFiles
  industry: IAccountIndustryBaseWithId | null
  selectedImpacts: IAccountFormSelectedImpacts[]
}

export interface IAccountFormUpdate extends IAccountFormCreate {
  owner_email: string | null
}

export interface IAccountFormSelectedImpacts {
  id: string
  home_page_show: boolean
  impact_page_show: boolean
  account_page_show: boolean
}

export interface IAccountCreateFormDto extends IAccountFormCreate {}

export interface IAccountUpdateFormDto
  extends Omit<IAccountFormUpdate, 'owner_email' | 'owner_name'>,
    Pick<IAccountBase, 'image_small' | 'image_medium'> {
  oldIcon: string
  oldMedia: IMediaRespDto
  locale: string
}

export type IAccountFormBase = Pick<
  IAccountFormUpdate,
  | 'account_holder'
  | 'email'
  | 'phone'
  | 'billing_email'
  | 'contact_person'
  | 'description'
  | 'address'
  | 'latitude'
  | 'longitude'
  | 'region'
  | 'country'
  | 'website'
  | 'gln'
>

export interface IAccountCreateDto extends IAccountFormBase {
  image: string | null
  image_small: string | null
  image_medium: string | null
  media: IMediaBase[]
  industry: IAccountIndustryBaseWithId | null
}

export interface IAccountUpdateDto extends IAccountFormBase {
  image: string | null
  image_small: string | null
  image_medium: string | null
  media: IMediaBase[]
  industry: IAccountIndustryBaseWithId | null
  locale: string
}

export interface IUpdateAccountAnalyticsDto {
  analytics_code: string | null
}

export interface IUpdateAccountAnalyticsForm {
  analytics_code: string | null
}

export interface IUpdateAccountWelcomeShowDto {
  show_welcome: boolean
}

export interface IUpdateAccountWelcomeShowForm {
  show_welcome: boolean
}

export interface IAccountByIdDto extends IAccountBaseWithId {
  suspended: boolean
  users: IAccountUserResponseDto[]
}

export interface IAccountsRespDto {
  count: number
  data: Array<IAccountByIdDto>
}

export interface IAccountsAllResp extends IAccountBaseWithId {
  owner: {
    id: string
    user: {
      id: string
      email: string
    } | null
  } | null
  users: IAccountUserResponseDto[]
}

export interface IAccountsAllRespDto {
  count: number
  data: Array<IAccountsAllResp>
  freeCount: number
  plusCount: number
  premiumCount: number
  enterpriseCount: number
}

export interface IAccountUserResponseDto {
  id: string
  roles: EAccountRoles[]
  user: Pick<IProfileUserDtoWithId, 'name' | 'image' | 'image_small' | 'id' | 'image_medium'> & {
    email: string
  }
  created_at: Date
  updated_at: Date
  status: EAccountStatuses
  activation_token_sent_at: Date | null
  steps: IAccountUserStep[]
  impacts: IAccountUserImpact[]
}

export interface IAccountUserStep
  extends Pick<
    IStepBaseWithId,
    'id' | 'image' | 'image_small' | 'image_medium' | 'name' | 'code'
  > {}

export interface IAccountUserImpact
  extends Pick<
    IImpactBaseWithId,
    'id' | 'image' | 'image_small' | 'image_medium' | 'name' | 'code'
  > {}

export interface IAccountScannerRespDto {
  id: string
  user: Pick<IProfileUserDtoWithId, 'name' | 'image' | 'image_small' | 'id' | 'image_medium'> & {
    email: string
  }
  status: EAccountStatuses
}

export enum EAccountSubscriptionPlans {
  free = 'free',
  plus = 'plus',
  premium = 'premium',
  enterprise = 'enterprise',
}

export enum EAccountRoles {
  owner = 'owner',
  admin = 'admin',
  // user = 'user',
  scanner = 'scanner',
}

export enum EAccountStatuses {
  active = 'active',
  pending = 'pending',
  deactivated = 'deactivated',
}

export interface IAccountUserInviteDto {
  email: string
  roles: EAccountRoles[]
}

export interface IAccountScannerInviteDto {
  email: string
}

export interface IAccountScannerInviteRespDto extends IMessageResponseDto {
  id: string
}

export interface IAccountUserInviteRespDto {
  verify_transfer_ownership_token?: string
  message: string
}

export interface IAccountUserInviteFormDto {
  email: string
  role: EAccountRoles
}

export interface IAccountRemoveUsersDto {
  ids: string[]
}

export interface IAccountUpdateUserRolesDto {
  roles: EAccountRoles[]
  id: string
}

export interface IAccountUpdateOwnerManualDto {
  email: string
}

export interface IAccountUpdateUserRolesRespDto {
  message: string
  verify_transfer_ownership_token?: string
}

export interface IAcceptAccountUserInviteRespDto {
  message: string
  id: string
  status: EAccountStatuses
  account: {
    id: string
  }
  user: {
    email: string
    agreement: boolean
    agreement_with_account: boolean
    id: string
  }
  registrationToken?: string
}

export interface IAccountSuspensionDto {
  id: string
  suspended: boolean
}

export interface IAccountEnterpriseDto {
  id: string
  assign: boolean
}

export interface IAccountByIdRawRespDto
  extends Pick<
      IAccountBaseWithId,
      'account_holder' | 'id' | 'image' | 'image_small' | 'email' | 'image_medium'
    >,
    Partial<Pick<IAccountBaseWithId, 'website' | 'address' | 'ai_generated'>> {
  socials?: ISocialUsernames | null
}

export interface IAccountsAllRawRespDto {
  count: number
  data: IAccountByIdRawRespDto[]
}

export interface IAccountCreateCheckDto
  extends Pick<IAccountBase, 'account_holder' | 'gln' | 'website'> {}

export interface ISignupAccountCreateCheckDto
  extends Pick<IAccountBase, 'account_holder' | 'website'> {}

export interface IAccountCheckResp
  extends Pick<
    IAccountBaseWithId,
    'id' | 'address' | 'website' | 'account_holder' | 'region' | 'country' | 'gln'
  > {
  existMessage: string
  hasOwner: boolean
}

export type IAccountCreateCheckRespDto = null | IAccountCheckResp

export interface IAccountScannerCreateForm {
  email: string
}

export interface INFTMetadata {
  description: string
  external_url: string
  image: string
  name: string
  attributes: IERC721MetadataAttribute[]
}

export interface IERC721MetadataAttribute {
  trait_type: string
  value: string | number
  display_type?: string
}

export enum EDefaultLandingPage {
  HOME = 'home',
  JOURNEY = 'journey',
  IMPACT = 'impact',
  ACCOUNT = 'account',
}

export interface IAccountJourneySettings {
  hide_name: boolean
  hide_product_name: boolean
  map_satellite_view: boolean
  show_unsgd_home_page: boolean
  show_unsgd_impacts_page: boolean
  show_wallet_address: boolean
  show_bc_info: boolean
  default_landing_page: EDefaultLandingPage
}

export interface IUpdateAccountJourneySettingsDto {
  hide_name?: boolean
  hide_product_name?: boolean
  map_satellite_view?: boolean
  show_unsgd_home_page?: boolean
  show_unsgd_impacts_page?: boolean
  show_wallet_address?: boolean
  show_bc_info?: boolean
  default_landing_page?: EDefaultLandingPage
}

export type IAccountQrLinkRespDto = {
  override_url: string | null
} | null

export interface IUpdateAccountQrLinkDto {
  id: string
  overrideUrl: string | null
  code: string
}

export interface IUpdateAccountOverrideUrlForm {
  overrideUrl: string | null
}

export interface IUpdateAccountPublicStatusDto {
  public: boolean
}
